import { money } from "./foodFormatters";

/**
 * An invoice line as typed into the review / edit tables. Numbers stay strings
 * so a cleared input stays empty instead of turning into 0.
 *
 * "Price" (unitCost) is what the vendor charged per billed unit (a case, or a
 * lb on a weighed item). Quantity may be a usable amount (e.g. 100 churros per
 * case), so cost per unit is always line total ÷ quantity, which is also how
 * ingredient costs are worked out.
 */
export type LineDraft = {
  description: string;
  quantity: string;
  unit: string;
  unitCost: string;
  totalCost: string;
  ingredientId: number | null;
};

export type LineValues = {
  description: string;
  quantity: number;
  unit: string;
  unitCost: number;
  totalCost: number;
  ingredientId: number | null;
};

export function toLineDraft(line: Omit<LineValues, "ingredientId"> & { ingredientId?: number | null }): LineDraft {
  return {
    description: line.description,
    quantity: String(line.quantity),
    unit: line.unit,
    unitCost: String(line.unitCost),
    totalCost: String(line.totalCost),
    ingredientId: line.ingredientId ?? null,
  };
}

export const EMPTY_LINE: LineDraft = {
  description: "",
  quantity: "1",
  unit: "each",
  unitCost: "",
  totalCost: "",
  ingredientId: null,
};

function toNumber(value: string): number {
  return value.trim() === "" ? Number.NaN : Number(value);
}

/** Validated numbers for saving, or a message naming the line. */
export function parseLineDraft(
  draft: LineDraft,
): { ok: true; value: LineValues } | { ok: false; error: string } {
  const description = draft.description.trim();
  const name = description ? `"${description}"` : "a line";
  if (!description) return { ok: false, error: "Every line needs a description" };

  const quantity = toNumber(draft.quantity);
  if (!(quantity > 0)) return { ok: false, error: `Enter a quantity above 0 for ${name}` };

  const totalCost = toNumber(draft.totalCost);
  if (!Number.isFinite(totalCost)) return { ok: false, error: `Enter a total for ${name}` };

  // A blank price falls back to total ÷ quantity.
  const unitCostInput = toNumber(draft.unitCost);
  const unitCost = Number.isFinite(unitCostInput)
    ? unitCostInput
    : Math.round((totalCost / quantity) * 10000) / 10000;

  return {
    ok: true,
    value: { description, quantity, unit: draft.unit, unitCost, totalCost, ingredientId: draft.ingredientId },
  };
}

/** "$3.89 / lb", or "-" when quantity or total is missing. */
export function costPerUnitLabel(draft: Pick<LineDraft, "quantity" | "totalCost" | "unit">): string {
  const quantity = toNumber(draft.quantity);
  const total = toNumber(draft.totalCost);
  if (!(quantity > 0) || !Number.isFinite(total)) return "-";
  return `${money(total / quantity)} / ${draft.unit}`;
}

export function sumLineTotals(drafts: Pick<LineDraft, "totalCost">[]): number {
  const sum = drafts.reduce((acc, d) => {
    const n = toNumber(d.totalCost);
    return Number.isFinite(n) ? acc + n : acc;
  }, 0);
  return Math.round(sum * 100) / 100;
}

/** True when the lines and the invoice total differ by more than a cent. */
export function totalsDiffer(linesTotal: number, invoiceTotal: number | null): boolean {
  return invoiceTotal !== null && Math.abs(linesTotal - invoiceTotal) > 0.005;
}

export const INPUT_CLASS =
  "rounded-md border border-tribal-border bg-black/20 px-2 py-1.5 text-sm disabled:opacity-60";
export const SMALL_BUTTON_CLASS =
  "rounded bg-white/5 px-2 py-1 text-xs text-tribal-muted hover:text-white disabled:opacity-60";
